import {
  associate,
  filterObject,
  mapValues,
  partitionBy,
  removeUndefinedProperties,
} from '@ps-gui/shared-utils';
import { BatchDomain, CtBatchMetaData, CtDocument } from '@ps-gui/claims-terminal-shared';

import { cleanValues } from './clean-extracted-values';
import {
  PersistDataService,
  ExtractedDocumentDataSnapshot,
} from '../app-state/persist-data.service';
import {
  ExtractedDataSnapshot,
  ExtractedTablesByKey,
  ExtractedValue,
  ExtractedValuesByKey,
} from '../../models/extracted-values';
import { MetaDataDto, PatchDto } from '../../models/interfaces';
import {
  FieldGroupKey,
  getFieldGroupKeyByFieldKey,
  getFieldModel,
  getTableModel,
} from '../../models/extraction-models/extraction-models';

/**
 * Builds the full audit patch (batchFields + batchTables + _301Records) from a consolidated
 * snapshot. Used by `AuditCtBatchTransmissionService.enrichAudit` and by
 * `AuditOrderTransmissionService.createFullOrderRequestDto`.
 *
 * If `auditId` is given, it is attached to every `_301Record`. Pass it for the legacy
 * patch-after-audit-order flow; omit it for the FullAuditOrder bulk flow (the backend
 * will resolve the audit id).
 */
export async function buildAuditPatch(
  snapshot: ExtractedDataSnapshot,
  ctBatchDomain: BatchDomain,
  documents: CtDocument[],
  persistDataService: PersistDataService,
  metaData?: CtBatchMetaData,
  auditId?: string,
): Promise<PatchDto> {
  const { fields, tables } = snapshot;
  const patch = createAuditPatchDtoFromStoredData(
    cleanValues(removeUndefinedProperties(fields)),
    tables,
    ctBatchDomain,
  );
  const _301Records = await getConsolidated301Records(
    fields,
    documents,
    ctBatchDomain,
    persistDataService,
  );
  return {
    ...patch,
    metaData: mapRelevantMetaDataForBackend(metaData),
    _301Records: auditId ? _301Records.map((record) => ({ ...record, auditId })) : _301Records,
  };
}

function mapRelevantMetaDataForBackend(input?: CtBatchMetaData): MetaDataDto {
  if (input == null) return {};
  return {
    absOrdNrDlp: input.externeId,
  };
}

function createAuditPatchDtoFromStoredData(
  extractedValues: ExtractedValuesByKey,
  extractedTables: ExtractedTablesByKey,
  ctBatchDomain: BatchDomain,
  metaData?: MetaDataDto,
): Omit<PatchDto, '_301Records'> {
  const fieldGroupKeyByFieldKey = getFieldGroupKeyByFieldKey(ctBatchDomain);

  const fieldGroups: Record<FieldGroupKey, Record<string, ExtractedValue>> = partitionBy<
    string,
    ExtractedValue,
    string
  >(
    extractedValues,
    (fieldKey) =>
      // The right part is currently relevant for 'auftragsart' since it does not appear in any section
      fieldGroupKeyByFieldKey[fieldKey] ?? 'metadata_fields',
  );

  const batchTables = mapValues(extractedTables, (_, rows) =>
    rows.map((row) => ({
      ...mapValues(row.rowData, (_, cell) => cell.value),
      // TODO: This is a hack as long as https://eucon.atlassian.net/browse/PSFDE-229 is not done
      class: row.class ?? 'AMBULANT',
    })),
  );

  return { batchFields: fieldGroups, batchTables: batchTables, metaData };
}

async function getConsolidated301Records(
  fields: ExtractedValuesByKey,
  documents: CtDocument[],
  ctBatchDomain: BatchDomain,
  persistDataService: PersistDataService,
): Promise<object[]> {
  const _301DocumentIds = documents
    .filter((document) => document.documentClasses.includes('25_Q3_301ER_DATEN'))
    .map((document) => document.id);

  const consolidatedRecords = await Promise.all(
    _301DocumentIds.map((id) =>
      getConsolidated301Record(id, fields, ctBatchDomain, persistDataService),
    ),
  );

  return consolidatedRecords.filter((record) => record !== undefined);
}

// Note: This function is kept almost fully (except consolidator value -> index mapping) generic and can be reused
// for similar use cases (i.e. other document types for which data has to be transmitted per-document and
// which are excluded from normal consolidation thus not appearing in the consolidation snapshot).
async function getConsolidated301Record(
  _301DocumentId: string,
  fields: ExtractedValuesByKey,
  ctBatchDomain: BatchDomain,
  persistDataService: PersistDataService,
): Promise<object | undefined> {
  const snapshot = await persistDataService.getExtractedDocumentDataSnapshot(
    _301DocumentId,
    '25_Q3_301ER_DATEN',
  );

  // If all fields in a 301-er data snapshot are unset, it currently implies that the document was skipped.
  // TODO: Make this less fragile - e.g. save a flag which indicates that a document was skipped
  if (
    Object.values(snapshot.fields).every((value) => !value) &&
    Object.values(snapshot.tables).every((table) => table.length === 0)
  ) {
    return undefined;
  }

  const consolidators = Object.keys(snapshot.fields)
    .map((key) => getFieldModel(ctBatchDomain, key))
    .filter((model) => model.inlineConsolidatorForField);

  const consolidatorKeys = consolidators.map((consolidator) => consolidator.key);

  const filteredSnapshotFields = filterObject(
    snapshot.fields,
    (key, value) => !consolidatorKeys.includes(key) && value !== undefined && value !== null,
  );

  const schadendatum = (fields['kopfdaten_schadendatum'] as string) ?? '01.01.2010';
  const aufnahmeDatum = filteredSnapshotFields['301er_daten_aufnahmedatum'] ?? schadendatum;
  const entlassungsDatum = filteredSnapshotFields['301er_daten_entlassungsdatum'] ?? aufnahmeDatum;

  filteredSnapshotFields['301er_daten_aufnahmedatum'] = aufnahmeDatum;
  filteredSnapshotFields['301er_daten_entlassungsdatum'] = entlassungsDatum;
  filteredSnapshotFields['301er_daten_aufnahmeuhrzeit'] =
    filteredSnapshotFields['301er_daten_aufnahmeuhrzeit'] ?? '00:00';
  filteredSnapshotFields['301er_daten_entlassungsuhrzeit'] =
    filteredSnapshotFields['301er_daten_entlassungsuhrzeit'] ?? '00:00';
  filteredSnapshotFields['301er_daten_beatmungsstunden'] =
    filteredSnapshotFields['301er_daten_beatmungsstunden'] ?? 0;
  filteredSnapshotFields['301er_daten_tage_ohne_brechung'] =
    filteredSnapshotFields['301er_daten_tage_ohne_brechung'] ?? 0;
  filteredSnapshotFields['301er_daten_geschlecht'] =
    filteredSnapshotFields['301er_daten_geschlecht'] ?? 'U';
  filteredSnapshotFields['301er_daten_entgeltbereich'] =
    filteredSnapshotFields['301er_daten_entgeltbereich'] ?? 'DRG';

  // For each consolidator we overwrite the value of the associated field with its consolidation value
  for (const consolidator of consolidators) {
    const consolidatorValue = snapshot.fields[consolidator.key];
    if (!consolidatorValue) continue;
    // NOTE: This only makes sense for 301-er records, it has to be refactored if we reuse this method
    const isInlineConsolidator = [
      '301er_daten_aufnahmeanlass',
      '301er_daten_aufnahmegrund',
      '301er_daten_entlassungsgrund',
    ].includes(consolidator.inlineConsolidatorForField!);
    filteredSnapshotFields[consolidator.inlineConsolidatorForField!] =
      isInlineConsolidator && consolidator.transformConsolidationValue
        ? consolidator.transformConsolidationValue(consolidatorValue as string)
        : consolidatorValue;
  }

  const icd10Codes: string[] = [];

  const diagnosen = rowsToTableDto(snapshot, 'diagnosen', ctBatchDomain)
    .sort((diagnose) => {
      const hauptdiagnose = (diagnose['hauptdiagnose'] as string) === 'true';
      return hauptdiagnose ? -1 : 1;
    })
    .filter((diagnose) => {
      const icd10Code = (diagnose['diagnosen_icd10_code'] as string).toUpperCase();
      if (icd10Codes.includes(icd10Code)) return false;
      icd10Codes.push(icd10Code);
      return true;
    });
  return {
    ...cleanValues(filteredSnapshotFields),
    diagnosen,
    prozeduren: rowsToTableDto(snapshot, 'operationen_und_prozeduren', ctBatchDomain),
  };
}

function rowsToTableDto(
  snapshot: ExtractedDocumentDataSnapshot,
  tableKey: string,
  ctBatchDomain: BatchDomain,
) {
  const tableModel = getTableModel(ctBatchDomain, tableKey);
  const consolidators =
    tableModel?.fieldModels?.filter((field) => field.inlineConsolidatorForField) ?? [];
  const consolidatorKeys = consolidators.map((consolidator) => consolidator.key);
  return (snapshot.tables[tableKey] ?? []).map((row) => {
    const consolidatedValuesByKey = associate(
      consolidators,
      (consolidator) => consolidator.inlineConsolidatorForField!,
      (consolidator) => row.rowData[consolidator.key].value,
    );
    const mappedValues = mapValues(
      filterObject(row.rowData, (key) => !consolidatorKeys.includes(key)),
      (key, row) =>
        Object.hasOwn(consolidatedValuesByKey, key) ? consolidatedValuesByKey[key] : row.value,
    );
    if (tableKey === 'diagnosen') {
      return { ...mappedValues, hauptdiagnose: (row.class === 'HAUPTDIAGNOSE').toString() };
    }
    return mappedValues;
  });
}
